import type { ReactNode } from "react"
import { Typography, Box, Divider } from "@mui/material"
import { useQueryClient } from "@tanstack/react-query"
import { makeStyles } from "tss-react/mui"
import { MeetingDetailsOrder } from "../../../../../../nonAdminUser/reports/home/meetingServiceApi"
import { OrderNoteAlert } from "../../../components/OrderNoteAlert"
import DeliveryDetails from "./DeliveryDetails"
import ContactDetails from "./ContactDetails"
import OrderItemsTable from "../slipModal/OrderItemsTable"
import { useDateTime } from "../../../../../../localization/useDateTime"
import { useAppLocalization } from "../../../../../../localization/components/AppLocalizationProvider"
import { useMemo, useState } from "react"
import { OrderChatStatusCard } from "../../../../../../../shared/components/orderChat/OrderChatStatusCard"
import { OrderChatDrawer } from "../../../../../../../shared/components/orderChat/OrderChatDrawer"
import { ChatMessageAuthorRole, UserType } from "../../../../../../../shared/components/orderChat/orderChatTypes"
import { useAppSelector } from "../../../../../../store/storeHooks"
import { selectEffectiveCompanyId, selectEffectiveUserId } from "../../../../../../context/contextSelectors"
import { MerchantMeetingServiceOrdersQuery } from "../../meetingServiceApi"
import { useMatchEnvironment } from "../../../../../../environment/envHooks"
import { PRINT_CHAT_ENVIRONMENTS } from "../../../../../../../shared/components/orderChat/print/usePrintWithChatHistory"
import { PrintViewOrderActions } from "../../../../components/receiptAndSlip/PrintViewOrderActions"
import { withOrderLinesGrouped } from "../../../../../../../shared/components/orderLines/orderLinesLib"

const useStyles = makeStyles()((theme) => ({
    orderContainer: {
        display: "flex",
        flexWrap: "wrap",
        alignItems: "flex-start",
        gap: "1rem",
        "&:last-child": {
            marginBottom: 0,
        },
        "@media print": {
            display: "block",
            breakAfter: "page",
            "&:last-child": {
                breakAfter: "auto",
            },
            marginBottom: 0,
            breakInside: "avoid",
        },
    },
    chatStatus: {
        flexBasis: "100%",
    },
    orderDivider: {
        borderStyle: "dashed",
        borderColor: theme.palette.divider,
    },
    orderId: {
        fontWeight: 600,
        color: theme.palette.tailwind.slate[700],
        marginBottom: theme.spacing(0.5),
    },
    mainContent: {
        flex: 1,
        minWidth: 0,
        display: "flex",
        gap: theme.spacing(4),
        [theme.breakpoints.down("md")]: {
            flexDirection: "column",
        },
        "@media print": {
            gap: theme.spacing(2),
            display: "flex",
            flexDirection: "row",
            breakInside: "avoid",
        },
    },
    leftColumn: {
        flex: "0 0 40%",
        maxWidth: "40%",
        "@media print": {
            flex: "0 0 50%",
            maxWidth: "50%",
            breakInside: "avoid",
        },
    },
    rightColumn: {
        flex: `0 0 calc(60% - ${theme.spacing(4)})`,
        maxWidth: `calc(60% - ${theme.spacing(4)})`,
        "@media print": {
            flex: `0 0 calc(50% - ${theme.spacing(2)})`,
            maxWidth: `calc(50% - ${theme.spacing(2)})`,
            breakInside: "avoid",
        },
    },
    orderName: {
        fontWeight: 400,
        fontSize: "1.125rem",
        color: theme.palette.tailwind.slate[500],
    },
    noteSection: {
        marginTop: theme.spacing(2),
    },
}))

type SingleOrderProps = Readonly<{
    order: MeetingDetailsOrder
    itemIndex?: number
    totalOrders: number
    orderMenu?: ReactNode
}>

export default function SingleOrder({ order, itemIndex = 0, totalOrders, orderMenu }: SingleOrderProps) {
    const { classes } = useStyles()
    const dateTimeFactory = useDateTime()
    const { l10n } = useAppLocalization()
    const queryClient = useQueryClient()
    const merchantId = useAppSelector(selectEffectiveCompanyId)
    const userId = useAppSelector(selectEffectiveUserId)
    const [isChatOpen, setIsChatOpen] = useState(false)
    const isPrintChatEnabled = useMatchEnvironment(PRINT_CHAT_ENVIRONMENTS)

    const deliveryTypeLabel = useMemo(() => {
        return l10n.getStringForEnum("delivery-type-filter", order.deliveryDetails.type, order.deliveryDetails.type)
    }, [order.deliveryDetails.type, l10n])

    const mapTableOrders = useMemo(() => {
        return withOrderLinesGrouped(order.deliveries).flatMap((delivery) =>
            delivery.orderLines.map((orderLine) => ({
                id: orderLine.id,
                items: orderLine.items,
                name: orderLine.name,
                deliveryTime: orderLine.deliveryTime,
            })),
        )
    }, [order])

    return (
        <>
            <Box className={classes.orderContainer}>
                {isPrintChatEnabled && (
                    <OrderChatStatusCard
                        orderId={order.id}
                        viewerRole={ChatMessageAuthorRole.MERCHANT}
                        hasChat={Boolean(order.messages?.length)}
                        orderMessages={order.messages}
                        onClick={() => setIsChatOpen(true)}
                        className={classes.chatStatus}
                    />
                )}
                <Box className={classes.mainContent}>
                    <Box className={classes.leftColumn}>
                        <Box>
                            <Typography variant="h6" className={classes.orderId}>
                                {deliveryTypeLabel}:{" "}
                                {dateTimeFactory.formatDateTime(dateTimeFactory.fromApi(order.deliveryDetails.time))}
                            </Typography>
                            <Typography variant="body1" className={classes.orderName}>
                                #{order?.id} - {order?.meetingDetails?.meetingName}
                            </Typography>
                        </Box>
                        <DeliveryDetails order={order} />
                        <ContactDetails order={order} />
                    </Box>

                    <Box className={classes.rightColumn}>
                        <OrderItemsTable orders={mapTableOrders} />
                        {!!order.note && <OrderNoteAlert note={order.note} className={classes.noteSection} />}
                    </Box>
                </Box>
                <PrintViewOrderActions orderMenu={orderMenu} />
            </Box>

            {itemIndex < totalOrders - 1 && <Divider className={classes.orderDivider} />}

            {isChatOpen && (
                <OrderChatDrawer
                    open
                    raised
                    onClose={() => setIsChatOpen(false)}
                    orderId={order.id}
                    orderTitle={order.meetingDetails?.meetingName}
                    viewerRole={ChatMessageAuthorRole.MERCHANT}
                    sender={userId && merchantId ? { type: UserType.MERCHANT, merchantId, userId } : undefined}
                    onMessageSent={() =>
                        queryClient.invalidateQueries({ queryKey: [MerchantMeetingServiceOrdersQuery.MEETING_SERVICE] })
                    }
                />
            )}
        </>
    )
}
